import { currencyFormatter } from "@common/helpers/currency";
import {
    monetaryValuesEqual,
    subtractMonetaryValues,
    sumMonetaryValues,
} from "@common/helpers/monetary";
import { pluralize } from "@common/helpers/string";
import type { AccrualImporterRow, AccrualMode } from "../types";
import { getInvoiceGroupSlices, type InvoiceGroupSlice } from "./groupHelpers";

const LINE_ITEMS_DONT_SUM_BASE_MESSAGE =
    "Line items do not sum to the document total";

function getLineItemsDontSumMessage(discrepancyAmount: number): string {
    return `${LINE_ITEMS_DONT_SUM_BASE_MESSAGE}. Discrepancy: ${currencyFormatter.format(discrepancyAmount)}`;
}

const MISSING_ENTITY_MESSAGE = "Entity is required";
const MISSING_COUNTERPARTY_MESSAGES: Record<AccrualMode, string> = {
    invoice: "Customer is required",
    bill: "Vendor is required",
};
const NO_LINE_ITEMS_MESSAGE =
    "At least one line item with a non-zero amount is required";

export function getInvoiceGroupWarnings({
    group,
    mode,
}: {
    group: InvoiceGroupSlice;
    mode: AccrualMode;
}): string[] {
    const warnings: string[] = [];
    const headerRow = group.headerRow;

    if (headerRow.extractionWarnings?.length) {
        warnings.push(...headerRow.extractionWarnings);
    }

    if (!headerRow.entityId) {
        warnings.push(MISSING_ENTITY_MESSAGE);
    }

    if (!headerRow.counterpartyId) {
        warnings.push(MISSING_COUNTERPARTY_MESSAGES[mode]);
    }

    const hasNonZeroLineItem = group.lineRows.some(
        (row) => row.amount !== null && row.amount !== 0,
    );
    if (!hasNonZeroLineItem) {
        warnings.push(NO_LINE_ITEMS_MESSAGE);
    }

    const totalAmount = headerRow.totalAmount;
    if (totalAmount !== null && totalAmount !== undefined) {
        const lineItemsSum = sumMonetaryValues([
            ...group.lineRows.map((row) => row.amount ?? 0),
            headerRow.tax ?? 0,
        ]);
        if (!monetaryValuesEqual(lineItemsSum, totalAmount)) {
            const discrepancyAmount = subtractMonetaryValues(
                totalAmount,
                lineItemsSum,
            );
            warnings.push(getLineItemsDontSumMessage(discrepancyAmount));
        }
    }

    return [...new Set(warnings)];
}

export function getInvoiceGroupSlicesWithWarnings({
    rows,
    mode,
}: {
    rows: AccrualImporterRow[];
    mode: AccrualMode;
}): InvoiceGroupSlice[] {
    return getInvoiceGroupSlices(rows).map((group) => ({
        ...group,
        warnings: getInvoiceGroupWarnings({ group, mode }),
    }));
}

export function getInvoiceGroupsWarningSummary({
    groups,
    accrualNoun,
}: {
    groups: InvoiceGroupSlice[];
    accrualNoun: AccrualMode;
}): {
    groupsWithWarningsCount: number;
    totalGroups: number;
    summaryLabel: string;
} {
    const groupsWithWarnings = groups.filter(
        (group) =>
            (
                group.warnings ??
                getInvoiceGroupWarnings({ group, mode: accrualNoun })
            ).length > 0,
    );
    const groupsWithWarningsCount = groupsWithWarnings.length;
    const totalGroups = groups.length;

    if (groupsWithWarningsCount === 0) {
        return {
            groupsWithWarningsCount: 0,
            totalGroups,
            summaryLabel: "",
        };
    }

    if (totalGroups === 1) {
        return {
            groupsWithWarningsCount,
            totalGroups,
            summaryLabel: `This ${accrualNoun} needs attention`,
        };
    }

    const pluralNoun = pluralize(accrualNoun, totalGroups);

    if (groupsWithWarningsCount === 1) {
        return {
            groupsWithWarningsCount,
            totalGroups,
            summaryLabel: `1 of ${totalGroups} ${pluralNoun} needs attention`,
        };
    }

    return {
        groupsWithWarningsCount,
        totalGroups,
        summaryLabel: `${groupsWithWarningsCount} of ${totalGroups} ${pluralNoun} need attention`,
    };
}
